import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Concepto, GastoDetalle } from '../types';
import gastosService from '../services/gastos.service';
import conceptsService from '../services/concepts.service';
import { formatCurrency } from '../utils/currency';

interface GastoFormData {
  numeroComprobante: string;
  fecha: string;
  beneficiario: string;
  concepto: string;
  valorTotal: number;
  observaciones: string;
  estado: 'pendiente' | 'pagado' | 'anulado';
  detalles: Omit<GastoDetalle, 'id' | 'gastoId' | 'createdAt' | 'updatedAt'>[];
}

const GastoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [formData, setFormData] = useState<GastoFormData>({
    numeroComprobante: '',
    fecha: new Date().toISOString().split('T')[0],
    beneficiario: '',
    concepto: '',
    valorTotal: 0,
    observaciones: '',
    estado: 'pendiente',
    detalles: [{
      conceptoId: 0,
      descripcion: '',
      cantidad: 1,
      precioUnitario: 0,
      descuentoPct: 0,
      subtotalBruto: 0,
      montoDescuento: 0,
      subtotalNeto: 0,
    }],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Cargar conceptos de tipo gasto
        const conceptosData = await conceptsService.getConceptosActivos();
        const conceptosGasto = conceptosData.filter(c => 
          c.rubroPresupuestal?.tipo === 'gasto'
        );
        setConceptos(conceptosGasto || []);

        // Generar número de comprobante si es nuevo
        if (!isEditing) {
          const year = new Date().getFullYear();
          const numero = await gastosService.generateNumeroComprobante(year);
          setFormData(prev => ({ ...prev, numeroComprobante: numero }));
        }

        // Si estamos editando, cargar el gasto
        if (isEditing && id) {
          const gasto = await gastosService.getGastoById(parseInt(id));
          setFormData({
            numeroComprobante: gasto.numeroComprobante,
            fecha: gasto.fecha.split('T')[0],
            beneficiario: gasto.beneficiario,
            concepto: gasto.concepto,
            valorTotal: gasto.valorTotal,
            observaciones: gasto.observaciones || '',
            estado: gasto.estado,
            detalles: gasto.detalles.map(d => ({
              conceptoId: d.conceptoId,
              descripcion: d.descripcion,
              cantidad: d.cantidad,
              precioUnitario: d.precioUnitario,
              descuentoPct: d.descuentoPct,
              subtotalBruto: d.subtotalBruto,
              montoDescuento: d.montoDescuento,
              subtotalNeto: d.subtotalNeto,
            })),
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
      }
    };
    fetchData();
  }, [id, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Actualizar el valor total cuando cambien los detalles
  useEffect(() => {
    const totalNeto = formData.detalles.reduce((sum, d) => sum + d.subtotalNeto, 0);
    setFormData(prev => ({
      ...prev,
      valorTotal: totalNeto
    }));
  }, [formData.detalles]);

  const agregarDetalle = () => {
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, {
        conceptoId: 0,
        descripcion: '',
        cantidad: 1,
        precioUnitario: 0,
        descuentoPct: 0,
        subtotalBruto: 0,
        montoDescuento: 0,
        subtotalNeto: 0,
      }]
    }));
  };

  const eliminarDetalle = (index: number) => {
    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
  };

  const handleConceptoChange = (index: number, conceptoId: string) => {
    const concepto = conceptos.find(c => c.id === parseInt(conceptoId));
    if (concepto) {
      const nuevosDetalles = [...formData.detalles];
      const detalle = nuevosDetalles[index];
      
      detalle.conceptoId = concepto.id;
      detalle.descripcion = concepto.nombre;
      detalle.precioUnitario = concepto.precioLista;
      
      // Recalcular totales
      const cantidad = detalle.cantidad;
      const precioUnitario = concepto.precioLista;
      const descuentoPct = detalle.descuentoPct || 0;
      
      detalle.subtotalBruto = cantidad * precioUnitario;
      detalle.montoDescuento = (detalle.subtotalBruto * descuentoPct) / 100;
      detalle.subtotalNeto = detalle.subtotalBruto - detalle.montoDescuento;
      
      setFormData(prev => ({ ...prev, detalles: nuevosDetalles }));
    }
  };

  const actualizarDetalle = (index: number, campo: keyof GastoDetalle, valor: any) => {
    const nuevosDetalles = [...formData.detalles];
    const detalle = nuevosDetalles[index];
    
    (detalle as any)[campo] = valor;
    
    // Recalcular totales
    const cantidad = parseFloat(detalle.cantidad.toString()) || 0;
    const precioUnitario = parseFloat(detalle.precioUnitario.toString()) || 0;
    const descuentoPct = parseFloat(detalle.descuentoPct.toString()) || 0;
    
    detalle.subtotalBruto = cantidad * precioUnitario;
    detalle.montoDescuento = (detalle.subtotalBruto * descuentoPct) / 100;
    detalle.subtotalNeto = detalle.subtotalBruto - detalle.montoDescuento;
    
    setFormData(prev => ({ ...prev, detalles: nuevosDetalles }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones
      if (!formData.numeroComprobante.trim()) {
        alert('El número de comprobante es requerido');
        return;
      }
      if (!formData.beneficiario.trim()) {
        alert('El beneficiario es requerido');
        return;
      }
      if (!formData.concepto.trim()) {
        alert('El concepto es requerido');
        return;
      }
      if (formData.detalles.length === 0) {
        alert('Debe agregar al menos un detalle');
        return;
      }
      if (formData.detalles.some(d => d.conceptoId === 0)) {
        alert('Todos los detalles deben tener un concepto seleccionado');
        return;
      }

      const submitData = {
        numeroComprobante: formData.numeroComprobante,
        fecha: formData.fecha,
        beneficiario: formData.beneficiario,
        concepto: formData.concepto,
        valorTotal: formData.valorTotal,
        observaciones: formData.observaciones || undefined,
        estado: formData.estado,
        detalles: formData.detalles.map(d => ({
          conceptoId: d.conceptoId,
          descripcion: d.descripcion,
          cantidad: d.cantidad,
          precioUnitario: d.precioUnitario,
          descuentoPct: d.descuentoPct || 0,
          subtotalBruto: d.subtotalBruto,
          montoDescuento: d.montoDescuento,
          subtotalNeto: d.subtotalNeto,
        })),
      };

      if (isEditing && id) {
        await gastosService.updateGasto(parseInt(id), submitData);
      } else {
        await gastosService.createGasto(submitData);
      }
      navigate('/gastos');
    } catch (error: any) {
      console.error('Error saving gasto:', error);
      const msg = error?.response?.data?.message || error?.message || 'Error al guardar el gasto';
      alert(Array.isArray(msg) ? msg.join('\n') : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/gastos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a gastos
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Gasto' : 'Nuevo Comprobante de Egreso'}
        </h1>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información General */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Información General</h2>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Número de Comprobante */}
            <div>
              <label htmlFor="numeroComprobante" className="block text-sm font-medium text-gray-700 mb-2">
                Número de Comprobante *
              </label>
              <input
                type="text"
                id="numeroComprobante"
                name="numeroComprobante"
                required
                value={formData.numeroComprobante}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="EGR-2025-0001"
              />
            </div>

            {/* Fecha */}
            <div>
              <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 mb-2">
                Fecha *
              </label>
              <input
                type="date"
                id="fecha"
                name="fecha"
                required
                value={formData.fecha}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Beneficiario */}
            <div>
              <label htmlFor="beneficiario" className="block text-sm font-medium text-gray-700 mb-2">
                Beneficiario *
              </label>
              <input
                type="text"
                id="beneficiario"
                name="beneficiario"
                required
                value={formData.beneficiario}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Nombre del proveedor o beneficiario"
              />
            </div>

            {/* Estado */}
            <div>
              <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-2">
                Estado *
              </label>
              <select
                id="estado"
                name="estado"
                required
                value={formData.estado}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
                <option value="anulado">Anulado</option>
              </select>
            </div>

            {/* Concepto */}
            <div className="sm:col-span-2">
              <label htmlFor="concepto" className="block text-sm font-medium text-gray-700 mb-2">
                Concepto General *
              </label>
              <input
                type="text"
                id="concepto"
                name="concepto"
                required
                value={formData.concepto}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ej: Pago de servicios profesionales"
              />
            </div>

            {/* Observaciones */}
            <div className="sm:col-span-2">
              <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="observaciones"
                name="observaciones"
                rows={3}
                value={formData.observaciones}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Información adicional..."
              />
            </div>
          </div>
        </div>

        {/* Detalles del Gasto */}
        <div className="bg-white shadow sm:rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Detalles del Gasto</h2>
            <button
              type="button"
              onClick={agregarDetalle}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Agregar línea
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Concepto</th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Cant.</th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Precio Unit.</th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Desc. %</th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Subtotal</th>
                  <th scope="col" className="relative px-3 py-3 w-16">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formData.detalles.map((detalle, index) => (
                  <tr key={index}>
                    <td className="px-3 py-4">
                      <select
                        value={detalle.conceptoId || ''}
                        onChange={(e) => handleConceptoChange(index, e.target.value)}
                        className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        required
                      >
                        <option value="">Seleccionar...</option>
                        {conceptos.map((concepto) => (
                          <option key={concepto.id} value={concepto.id}>
                            {concepto.codigo ? `${concepto.codigo} - ` : ''}{concepto.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="text"
                        value={detalle.descripcion}
                        onChange={(e) => actualizarDetalle(index, 'descripcion', e.target.value)}
                        className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Descripción adicional..."
                      />
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={detalle.cantidad}
                        onChange={(e) => actualizarDetalle(index, 'cantidad', parseFloat(e.target.value) || 0)}
                        className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={detalle.precioUnitario}
                        onChange={(e) => actualizarDetalle(index, 'precioUnitario', parseFloat(e.target.value) || 0)}
                        className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </td>
                    <td className="px-3 py-4">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={detalle.descuentoPct}
                        onChange={(e) => actualizarDetalle(index, 'descuentoPct', parseFloat(e.target.value) || 0)}
                        className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </td>
                    <td className="px-3 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(detalle.subtotalNeto)}
                    </td>
                    <td className="px-3 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => eliminarDetalle(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                    Total:
                  </td>
                  <td className="px-3 py-3 text-right text-lg font-bold text-gray-900">
                    {formatCurrency(formData.valorTotal)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/gastos')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear Gasto')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GastoForm;
